import styled, { css } from 'styled-components';

// The "Likelihood" caption sits out of flow, so anything to its left pays that width back.
const AXIS_CAPTION_GUTTER = 22;

// The box is exactly the matrix, so headings, scores and the arrow centre on the grid.
export const GridLayout = styled.div`
  position: relative;
  display: inline-block;
`;

// vertical-rl makes justify-content the axis that centres against the rows, so both are set.
export const VerticalAxis = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
  font-size: 12px;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const HorizontalAxis = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 6px;
  font-size: 12px;
  text-align: center;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const MatrixGrid = styled.div<{ $columns: number; $cellSize: number }>`
  display: grid;
  gap: 3px;
  grid-template-columns: ${({ $columns, $cellSize }) => `repeat(${$columns}, ${$cellSize}px)`};
`;

export const MatrixCell = styled.button<{
  $bg: string;
  $color: string;
  $selected: boolean;
  $interactive: boolean;
  $size: number;
}>`
  padding: 0;
  height: ${({ $size }) => $size}px;
  border: none;
  border-radius: 4px;
  outline: ${({ $selected, theme }) => ($selected ? `3px solid ${theme.palette.common.black}` : 'none')};
  outline-offset: -1px;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  font-size: 12px;
  font-weight: ${({ $selected }) => ($selected ? 700 : 400)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  /* Unselected cells recede so the rated cell reads first, without losing the band. */
  opacity: ${({ $selected }) => ($selected ? 1 : 0.72)};
  transition: opacity 120ms ease, transform 120ms ease;

  /* Hover only where a cell can be picked: in edit mode the hover is the affordance. */
  ${({ $interactive }) =>
    $interactive &&
    css`
      &:hover {
        opacity: 1;
        transform: scale(1.08);
        z-index: 2;
      }
    `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: 1px;
  }
`;

export const PanelScroller = styled.div`
  width: 100%;
  overflow-x: auto;
`;

export const PanelLayout = styled.div<{ $columns: number }>`
  display: grid;
  /* Shrink-wrap and centre: auto columns would push the matrices to the section's edges. */
  width: fit-content;
  margin: 0 auto;
  grid-template-columns: ${({ $columns }) => `repeat(${$columns}, auto)`};
  grid-template-rows: auto auto auto;
  align-items: center;
  justify-items: center;
  column-gap: 8px;
  row-gap: 10px;
  /* Left padding reserves the "Likelihood" caption; the section spaces the Save row itself. */
  padding: 8px 16px 0 ${AXIS_CAPTION_GUTTER}px;
`;

/* A heading for its matrix, so it reads at heading size rather than as a caption. */
export const MatrixColumnTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const ScoreTransition = styled.div`
  display: flex;
  gap: 2px;
  flex-direction: column;
  align-items: center;
  /* Reserved both ways so the matrices hold still as the reduction changes width. */
  min-height: 44px;
  min-width: 60px;
  margin-right: ${AXIS_CAPTION_GUTTER}px;
  justify-content: center;
  color: ${({ theme }) => theme.palette.text.disabled};
`;

export const DashedArrowColor = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.palette.grey[500]};
`;

// The reduction is the point of the pairing, so it outranks the arrow carrying it.
export const ScoreDelta = styled.span<{ $improved: boolean }>`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  color: ${({ $improved, theme }) => ($improved ? theme.palette.success.dark : theme.palette.error.main)};
`;

// Rendered rather than left blank, so a zero reduction reads as deliberate.
export const NoChangeLabel = styled.span`
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const ScoreSummaryRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  /* Clears the out-of-flow "Severity" caption sitting under the matrix. */
  margin-top: 22px;
  min-height: 40px;
`;

export const BandName = styled.span`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  /* Fits the longest band name, so the score chip does not slide when the band changes. */
  min-width: 74px;
  text-align: left;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const ScoreBadge = styled.div<{ $bg: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  height: 40px;
  padding: 0 12px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 18px;
  background-color: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

export const UnratedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 40px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;
